/**
 * API Service for FracFocus Generator
 * Based on the VBA module Data_Pull_Parse
 */


// Backend URL
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

// API endpoints
const API_ENDPOINTS = {
  FRACPRO_TOKEN: '/api/fracpro/token',
  TREATMENT_REPORT: '/api/fracpro/treatment-report',
  CHEMICALS_REPORT: '/api/fracpro/chemicals-report',
  PETRIX_DATA: '/api/fracpro/petrix-data',
  FRACTURE_JOB: '/api/fracpro/fracture-job',
  START_END_TIME: '/api/fracpro/start-end-time',
  PROCESS_JOB_DATA: '/api/fracpro/process-job-data',
  AZURE_STORAGE_CONFIG: '/api/azure-storage-config'
};

/**
 * Check if token needs refresh and refresh it if needed
 * @returns {Promise<string>} The valid token
 */
const checkAndGetValidToken = async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No authentication token found');
    }
    
    // Check if token is expired or about to expire
    const response = await fetch(`${BACKEND_URL}/verify-token`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    const data = await response.json();
    
    // If token is expired or about to expire, refresh it
    if (!response.ok || data.needsRefresh) {
      console.log('Token needs refresh, attempting to refresh...');
      const refreshResponse = await fetch(`${BACKEND_URL}/refresh-token`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const refreshData = await refreshResponse.json();
      
      if (refreshResponse.ok && refreshData.token) {
        console.log('Token refreshed successfully');
        // Update token in localStorage
        localStorage.setItem('token', refreshData.token);
        
        // Update auth in localStorage
        const savedAuth = localStorage.getItem('auth');
        if (savedAuth) {
          const parsedAuth = JSON.parse(savedAuth);
          parsedAuth.token = refreshData.token;
          localStorage.setItem('auth', JSON.stringify(parsedAuth));
        }
        
        return refreshData.token;
      } else {
        console.error('Failed to refresh token');
        throw new Error('Failed to refresh authentication token');
      }
    }
    
    return token;
  } catch (error) {
    console.error('Token verification error:', error);
    throw error;
  }
};

/**
 * Get bearer token for FracPro API
 * @returns {Promise<string>} The bearer token
 */
export const getBearerToken = async () => {
  try {
    // Get a valid token (refreshed if needed)
    const token = await checkAndGetValidToken();
    
    const response = await fetch(`${BACKEND_URL}${API_ENDPOINTS.FRACPRO_TOKEN}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });
    
    const data = await response.json();
    
    if (data.error) {
      throw new Error(data.error);
    }
    
    return data.token;
  } catch (error) {
    console.error('Error getting bearer token:', error);
    throw error;
  }
};

/**
 * Get treatment report data
 * @param {string} jobNumber - The job number
 * @returns {Promise<Object>} The treatment report data
 */
export const getTreatmentReport = async (jobNumber) => {
  try {
    // Get a valid token (refreshed if needed)
    const token = await checkAndGetValidToken();
    
    const response = await fetch(`${BACKEND_URL}${API_ENDPOINTS.TREATMENT_REPORT}?jobNumber=${jobNumber}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });
    
    return await response.json();
  } catch (error) {
    console.error('Error getting treatment report:', error);
    throw error;
  }
};

/**
 * Get chemicals and proppants used report
 * @param {string} jobNumber - The job number
 * @returns {Promise<Object>} The chemicals and proppants report data
 */
export const getChemicalsReport = async (jobNumber) => {
  try {
    // Get a valid token (refreshed if needed)
    const token = await checkAndGetValidToken();
    
    const response = await fetch(`${BACKEND_URL}${API_ENDPOINTS.CHEMICALS_REPORT}?jobNumber=${jobNumber}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });
    
    return await response.json();
  } catch (error) {
    console.error('Error getting chemicals report:', error);
    throw error;
  }
};

/**
 * Get Petrix data
 * @param {string} jobNumber - The job number
 * @returns {Promise<Object>} The Petrix data
 */
export const getPetrixData = async (jobNumber) => {
  try {
    // Get a valid token (refreshed if needed)
    const token = await checkAndGetValidToken();
    
    const response = await fetch(`${BACKEND_URL}${API_ENDPOINTS.PETRIX_DATA}?jobNumber=${jobNumber}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });
    
    return await response.json();
  } catch (error) {
    console.error('Error getting Petrix data:', error);
    throw error;
  }
};

/**
 * Get fracture job data
 * @param {string} jobNumber - The job number
 * @returns {Promise<Object>} The fracture job data
 */
export const getFractureJob = async (jobNumber) => {
  try {
    // Get a valid token (refreshed if needed)
    const token = await checkAndGetValidToken();
    
    const response = await fetch(`${BACKEND_URL}${API_ENDPOINTS.FRACTURE_JOB}?jobNumber=${jobNumber}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });
    
    return await response.json();
  } catch (error) {
    console.error('Error getting fracture job data:', error);
    throw error;
  }
};

/**
 * Get start/end time for a well
 * @param {string} wellApi - The well API number
 * @param {number} stage - The stage number
 * @param {string} timeType - The time type (timeStart or timeEnd)
 * @returns {Promise<string>} The formatted date/time
 */
export const getStartEndTime = async (wellApi, stage, timeType) => {
  try {
    // Get a valid token (refreshed if needed)
    const token = await checkAndGetValidToken();
    
    const response = await fetch(`${BACKEND_URL}${API_ENDPOINTS.START_END_TIME}?wellApi=${wellApi}&stage=${stage}&timeType=${timeType}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });
    
    const data = await response.json();
    
    if (data.error) {
      throw new Error(data.error);
    }
    
    return data.date;
  } catch (error) {
    console.error(`Error getting ${timeType}:`, error);
    throw error;
  }
};

/**
 * Process job data for the application
 * @param {string} jobNumber - The job number
 * @returns {Promise<Array>} Array of well data objects
 */
export const processJobData = async (jobNumber) => {
  try {
    console.log(`Processing job data for job number: ${jobNumber}`);
    
    // Get a valid token (refreshed if needed)
    const token = await checkAndGetValidToken();
    console.log('Token obtained successfully');
    
    const url = `${BACKEND_URL}${API_ENDPOINTS.PROCESS_JOB_DATA}?jobNumber=${jobNumber}`;
    console.log('Making API request to:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });
    
    console.log('API response status:', response.status);
    console.log('API response status text:', response.statusText);
    
    // Log response headers
    const headers = {};
    response.headers.forEach((value, name) => {
      headers[name] = value;
    });
    console.log('API response headers:', headers);
    
    const data = await response.json();
    console.log('API response data:', data);
    
    if (data.error) {
      console.error('API returned an error:', data.error);
      throw new Error(data.error);
    }
    
    return data;
  } catch (error) {
    console.error('Error processing job data:', error);
    throw error;
  }
};

/**
 * Get Azure Storage configuration
 * @returns {Promise<Object>} The Azure Storage configuration
 */
export const getAzureStorageConfig = async () => {
  try {
    // Get a valid token (refreshed if needed)
    const token = await checkAndGetValidToken();
    
    const response = await fetch(`${BACKEND_URL}${API_ENDPOINTS.AZURE_STORAGE_CONFIG}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });
    
    const data = await response.json();
    
    if (data.error) {
      throw new Error(data.error);
    }
    
    // The backend now returns accountName, containerName, and accountKey directly
    console.log(`Received Azure Storage configuration from backend for account: ${data.accountName}`);
    
    return data;
  } catch (error) {
    console.error('Error getting Azure Storage configuration:', error);
    throw error;
  }
};

const ApiService = {
  getBearerToken,
  getTreatmentReport,
  getChemicalsReport,
  getPetrixData,
  getFractureJob,
  getStartEndTime,
  processJobData,
  getAzureStorageConfig
};

export default ApiService;
